import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupLaboratoryPersonnelActions from "../../../actions/trialGroupLaboratoryPersonnels";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialLaboratoryPersonnelBreadcrumbs,
    GroupTrialLaboratoryPersonnelCreateBreadcrumbs,
    TrialGroupLaboratoryPersonnelBreadcrumbs,
    TrialGroupLaboratoryPersonnelCreateBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import {
    GroupTrialLabPersonnelSubMenu,
    TrialGroupLabPersonnelSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ITrialGroupLaboratoryPersonnelReduxFormState } from "../../../reducers/reactReduxForms/trialGroupLaboratoryPersonnel";

import "./TrialGroupLaboratoryPersonnelPage.scss";

interface TrialGroupLaboratoryPersonnelPageProps {
    form: Dtos.TrialGroupLaboratoryPersonnel & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupLaboratoryPersonnelReduxFormState;
    trialGroupLaboratoryPersonnel: Dtos.TrialGroupLaboratoryPersonnel;
    lookups: Dtos.Lookup[];
    loadingTrialGroupLaboratoryPersonnel: boolean;
    loadTrialGroupLaboratoryPersonnelSuccess: boolean;
    loadTrialGroupLaboratoryPersonnelFailure: boolean;
    loadTrialGroupLaboratoryPersonnelErrorCode: string;
    savingTrialGroupLaboratoryPersonnel: boolean;
    saveTrialGroupLaboratoryPersonnelSuccess: boolean;
    saveTrialGroupLaboratoryPersonnelFailure: boolean;
    creatingTrialGroupLaboratoryPersonnel: boolean;
    createTrialGroupLaboratoryPersonnelSuccess: boolean;
    createTrialGroupLaboratoryPersonnelFailure: boolean;
    validatingTrialGroupLaboratoryPersonnel: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelClearActionCreator;
    loadTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelLoadByIdActionCreator;
    saveTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelSaveActionCreator;
    createTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelCreateActionCreator;
    validateTrialGroupLaboratoryPersonnel: TrialGroupLaboratoryPersonnelActions.ITrialGroupLaboratoryPersonnelValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupLaboratoryPersonnel";

const TrialGroupLaboratoryPersonnelPage: FC<
    TrialGroupLaboratoryPersonnelPageProps
> = ({
    form,
    formState,
    formProperties,
    trialGroupLaboratoryPersonnel,
    lookups,
    loadingTrialGroupLaboratoryPersonnel,
    loadTrialGroupLaboratoryPersonnelFailure,
    loadTrialGroupLaboratoryPersonnelErrorCode,
    savingTrialGroupLaboratoryPersonnel,
    saveTrialGroupLaboratoryPersonnelSuccess,
    saveTrialGroupLaboratoryPersonnelFailure,
    creatingTrialGroupLaboratoryPersonnel,
    validatingTrialGroupLaboratoryPersonnel,
    validationFailures,
    permissions,
    clearTrialGroupLaboratoryPersonnel,
    loadTrialGroupLaboratoryPersonnel,
    saveTrialGroupLaboratoryPersonnel,
    createTrialGroupLaboratoryPersonnel,
    validateTrialGroupLaboratoryPersonnel,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const { trialGroupLaboratoryId } = useParams<{ trialGroupLaboratoryId: string; }>();
    const { trialGroupLaboratoryPersonnelId } = useParams<{ trialGroupLaboratoryPersonnelId: string; }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);
    const trialGroupLaboratoryIdNumber = parseInt(trialGroupLaboratoryId, 10);
    const trialGroupLaboratoryPersonnelIdNumber = parseInt(trialGroupLaboratoryPersonnelId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();


    // Load Trial Group Laboratory Personnel
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            groupIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratoryPersonnelIdNumber &&
            !trialGroupLaboratoryPersonnel
        ) {
            const drawerItemSetter =
                baseRoute === "trials"
                    ? TrialGroupLabPersonnelSubMenu
                    : GroupTrialLabPersonnelSubMenu;

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelBreadcrumbs
                    : GroupTrialLaboratoryPersonnelBreadcrumbs;

            setDrawerItems(
                drawerItemSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryPersonnelIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryPersonnelIdNumber
                )
            );

            loadTrialGroupLaboratoryPersonnel(
                trialGroupLaboratoryPersonnelIdNumber
            );
        } else if (
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratoryPersonnelIdNumber &&
            !trialGroupLaboratoryPersonnel
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelCreateBreadcrumbs
                    : GroupTrialLaboratoryPersonnelCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber
                )
            );
            setDrawerItems([]);
            createTrialGroupLaboratoryPersonnel(trialGroupLaboratoryIdNumber);
        } else if (trialGroupLaboratoryPersonnel && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryPersonnelIdNumber,
        trialGroupLaboratoryPersonnel,
        location,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        TrialGroupLabPersonnelSubMenu,
        GroupTrialLabPersonnelSubMenu,
        TrialGroupLaboratoryPersonnelBreadcrumbs,
        GroupTrialLaboratoryPersonnelBreadcrumbs,
        loadTrialGroupLaboratoryPersonnel,
        TrialGroupLaboratoryPersonnelCreateBreadcrumbs,
        GroupTrialLaboratoryPersonnelCreateBreadcrumbs,
        createTrialGroupLaboratoryPersonnel,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupLaboratoryPersonnelFailure) {
            navigate(`/error/${loadTrialGroupLaboratoryPersonnelErrorCode}`);
        }
    }, [
        loadTrialGroupLaboratoryPersonnelFailure,
        loadTrialGroupLaboratoryPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            trialGroupLaboratoryPersonnelIdNumber &&
            trialGroupLaboratoryPersonnel
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelBreadcrumbs
                    : GroupTrialLaboratoryPersonnelBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryPersonnelIdNumber,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.trialGroup?.trial?.studyAcronym,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.trialGroup?.group?.name,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.laboratory?.name,
                    trialGroupLaboratoryPersonnel?.personnel
                        ?.displayWithSalutation
                )
            );
        } else if (
            trialIdNumber &&
            trialGroupIdNumber &&
            trialGroupLaboratoryIdNumber &&
            !trialGroupLaboratoryPersonnelIdNumber &&
            trialGroupLaboratoryPersonnel
        ) {
            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoryPersonnelCreateBreadcrumbs
                    : GroupTrialLaboratoryPersonnelCreateBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroupLaboratoryIdNumber,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.trialGroup?.trial?.displayName,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.trialGroup?.group?.name,
                    trialGroupLaboratoryPersonnel?.trialGroupLaboratory
                        ?.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryPersonnelIdNumber,
        trialGroupLaboratoryPersonnel,
        location,
        setBreadcrumbs,
        TrialGroupLaboratoryPersonnelBreadcrumbs,
        GroupTrialLaboratoryPersonnelBreadcrumbs,
        TrialGroupLaboratoryPersonnelCreateBreadcrumbs,
        GroupTrialLaboratoryPersonnelCreateBreadcrumbs,
    ]);

    // Reload trial group laboratory personnel after saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryPersonnel &&
            saveTrialGroupLaboratoryPersonnelSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group laboratory personnel saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel`;
                navigate(url);
            } else if (!trialGroupLaboratoryPersonnelIdNumber) {
                const url: string =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel/${trialGroupLaboratoryPersonnel?.id}/details`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel/${trialGroupLaboratoryPersonnel?.id}/details`;

                navigate(url);
                clearTrialGroupLaboratoryPersonnel();
            } else {
                loadTrialGroupLaboratoryPersonnel(
                    trialGroupLaboratoryPersonnelIdNumber
                );
            }
        }
    }, [
        savingTrialGroupLaboratoryPersonnel,
        saveTrialGroupLaboratoryPersonnelSuccess,
        trialIdNumber,
        trialGroupIdNumber,
        trialGroupLaboratoryIdNumber,
        trialGroupLaboratoryPersonnelIdNumber,
        trialGroupLaboratoryPersonnel,
        clearTrialGroupLaboratoryPersonnel,
        loadTrialGroupLaboratoryPersonnel,
        saveAndReturn,
        location,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialGroupLaboratoryPersonnel &&
            saveTrialGroupLaboratoryPersonnelFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupLaboratoryPersonnel,
        saveTrialGroupLaboratoryPersonnelFailure,
    ]);

    // Cleanup effect to clear the laboratory personnel on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupLaboratoryPersonnel();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroupLaboratoryPersonnel, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupLaboratoryPersonnel =
                {} as Dtos.TrialGroupLaboratoryPersonnel;

            if (!data.trialGroupLaboratoryId) {
                changes.trialGroupLaboratoryId = trialGroupLaboratoryIdNumber;
            }

            await saveTrialGroupLaboratoryPersonnel(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.LaboratoryPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url: string =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${trialGroupLaboratoryIdNumber}/personnel`;

        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupLaboratoryPersonnel =
            {} as Dtos.TrialGroupLaboratoryPersonnel;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupLaboratoryPersonnel }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupLaboratoryPersonnel =
                {} as Dtos.TrialGroupLaboratoryPersonnel;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialGroupLaboratoryPersonnel
            ) {
                await validateTrialGroupLaboratoryPersonnel(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                trialGroupLaboratoryPersonnelIdNumber,
                permissions,
                Dtos.Permission.LabPersonnelUpdate,
                Dtos.Permission.LabPersonnelCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupLaboratoryPersonnel ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            personnelDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Personnel Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryPersonnel",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-1",
                                                fieldClassName: "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            roleDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Role Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryPersonnel",
                                        {
                                            roleId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                size: 2,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "trialGroupLaboratoryPersonnel",
                                                                "roleId",
                                                                lookups
                                                            ),
                                                            form.roleId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-1",
                                                fieldClassName: "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryPersonnel",
                                        {
                                            dateStarted: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupLaboratoryPersonnel",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: DatePickerFormInputFabric,
                                                inputDisabled: formDisabled || !form.personnelId,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialGroupLaboratoryPersonnel",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        trialGroupLaboratoryIdNumber,
        permissions,
        savingTrialGroupLaboratoryPersonnel,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        personnelSearchLoader
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabPersonnelView}
            loading={
                loadingTrialGroupLaboratoryPersonnel ||
                savingTrialGroupLaboratoryPersonnel ||
                creatingTrialGroupLaboratoryPersonnel
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoryPersonnelPageProps> => {
    let trialGroupLaboratoryPersonnel:
        | Dtos.TrialGroupLaboratoryPersonnel
        | undefined = !(
        state.trialGroupLaboratoryPersonnel.formData instanceof Array
    )
        ? state.trialGroupLaboratoryPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupLaboratoryPersonnel: trialGroupLaboratoryPersonnel!,
        loadingTrialGroupLaboratoryPersonnel:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Pending,
        loadTrialGroupLaboratoryPersonnelSuccess:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Success,
        loadTrialGroupLaboratoryPersonnelFailure:
            state.trialGroupLaboratoryPersonnel.loadState &&
            state.trialGroupLaboratoryPersonnel.loadState.status ==
                RequestState.Failure,
        loadTrialGroupLaboratoryPersonnelErrorCode:
            state.trialGroupLaboratoryPersonnel.loadState.errorCode,
        savingTrialGroupLaboratoryPersonnel:
            state.trialGroupLaboratoryPersonnel.saveState &&
            state.trialGroupLaboratoryPersonnel.saveState.status ==
                RequestState.Pending,
        saveTrialGroupLaboratoryPersonnelSuccess:
            state.trialGroupLaboratoryPersonnel.saveState &&
            state.trialGroupLaboratoryPersonnel.saveState.status ==
                RequestState.Success,
        saveTrialGroupLaboratoryPersonnelFailure:
            state.trialGroupLaboratoryPersonnel.saveState &&
            state.trialGroupLaboratoryPersonnel.saveState.status ==
                RequestState.Failure,
        creatingTrialGroupLaboratoryPersonnel:
            state.trialGroupLaboratoryPersonnel.createState &&
            state.trialGroupLaboratoryPersonnel.createState.status ==
                RequestState.Pending,
        createTrialGroupLaboratoryPersonnelSuccess:
            state.trialGroupLaboratoryPersonnel.createState &&
            state.trialGroupLaboratoryPersonnel.createState.status ==
                RequestState.Success,
        createTrialGroupLaboratoryPersonnelFailure:
            state.trialGroupLaboratoryPersonnel.createState &&
            state.trialGroupLaboratoryPersonnel.createState.status ==
                RequestState.Failure,
        validatingTrialGroupLaboratoryPersonnel:
            state.trialGroupLaboratoryPersonnel.validateState &&
            state.trialGroupLaboratoryPersonnel.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupLaboratoryPersonnel.lookups,
        form: state.reduxForms.trialGroupLaboratoryPersonnel,
        formState: state.trialGroupLaboratoryPersonnel.formState,
        formProperties: state.trialGroupLaboratoryPersonnel.formProperties,
        reduxFormState:
            state.reduxForms.formState.trialGroupLaboratoryPersonnel,
        validationFailures: trialGroupLaboratoryPersonnel
            ? state.trialGroupLaboratoryPersonnel.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoryPersonnelPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.LoadTrialGroupLaboratoryPersonnelById,
            dispatch
        ),
        clearTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.Clear,
            dispatch
        ),
        saveTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.SaveTrialGroupLaboratoryPersonnel,
            dispatch
        ),
        createTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.CreateTrialGroupLaboratoryPersonnel,
            dispatch
        ),
        validateTrialGroupLaboratoryPersonnel: bindActionCreators(
            TrialGroupLaboratoryPersonnelActions.ValidateTrialGroupLaboratoryPersonnel,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoryPersonnelPage);
